import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../utils/api';

function SignupPage({ onSignupSuccess }) {
const [formData, setFormData] = useState({
fullName: '',
email: '',
password: '',
confirmPassword: '',
agreedToTerms: false
});
const [errors, setErrors] = useState({});
const [loading, setLoading] = useState(false);
const [showLogin, setShowLogin] = useState(false);
const [showPasswordRules, setShowPasswordRules] = useState(false);
const [touched, setTouched] = useState({
fullName: false,
email: false,
password: false,
confirmPassword: false
});
const [modal, setModal] = useState({ open: false, title: '', message: '' });
const [forgotModalOpen, setForgotModalOpen] = useState(false);
const [forgotEmail, setForgotEmail] = useState('');
const [forgotResult, setForgotResult] = useState('');
const [forgotLoading, setForgotLoading] = useState(false);

const validateField = (name, value, allValues) => {
if (name === 'fullName') {
  if (!value.trim()) return 'Full name is required';
}
if (name === 'email') {
  if (!value.trim()) return 'Email is required';
  if (!/\S+@\S+\.\S+/.test(value)) return 'Email is invalid';
}
if (name === 'password') {
  if (!value) return 'Password is required';
  if (value.length < 6) return 'Password must be at least 6 characters';
}
if (name === 'confirmPassword') {
  if (!value) return 'Confirm password is required';
  if (value !== allValues.password) return 'Passwords do not match';
}
return null;
};

const validateForm = () => {
const newErrors = {};
const fields = ['fullName', 'email', 'password', 'confirmPassword'];
fields.forEach((field) => {
  const error = validateField(field, formData[field], formData);
  if (error) newErrors[field] = error;
});
if (!formData.agreedToTerms) newErrors.agreedToTerms = 'You must agree to the terms and privacy policy';
setErrors(newErrors);
return Object.keys(newErrors).length === 0;
};

const passwordChecks = {
minLength: formData.password.length >= 6,
hasLower: /[a-z]/.test(formData.password),
hasUpper: /[A-Z]/.test(formData.password),
hasDigit: /\d/.test(formData.password),
matchesConfirm:
  formData.confirmPassword.length > 0 &&
  formData.password === formData.confirmPassword
};

const handleSubmit = async (e) => {
e.preventDefault();

if (!validateForm()) return;

setLoading(true);
try {
  await axios.post(`${API_BASE}/account/register`, {
    fullName: formData.fullName,
    email: formData.email,
    password: formData.password,
    agreedToTerms: formData.agreedToTerms
  });

  const loginResponse = await axios.post(`${API_BASE}/account/login`, {
    email: formData.email,
    password: formData.password
  });

  localStorage.setItem('token', loginResponse.data.token);
  localStorage.setItem('userId', loginResponse.data.userId);
  localStorage.setItem('userEmail', loginResponse.data.email);
  localStorage.setItem('userName', loginResponse.data.fullName);
  localStorage.setItem('userNickname', loginResponse.data.nickname || '');
  localStorage.setItem('userRole', loginResponse.data.role || 'User');

  if (onSignupSuccess) {
    onSignupSuccess(loginResponse.data);
  }
} catch (error) {
  const errorMsg = error.response?.data?.message || 'Registration failed. Please try again.';
  const errorList =
    error.response?.data?.errors ||
    error.response?.data?.Errors ||
    [];
  setErrors({ submit: errorMsg, submitList: errorList });
  setModal({ open: true, title: 'Sign Up Failed', message: errorMsg });
}
setLoading(false);

};

const handleLogin = async (email, password) => {
try {
const response = await axios.post(`${API_BASE}/account/login`, {
email,
password
});

  localStorage.setItem('token', response.data.token);
  localStorage.setItem('userId', response.data.userId);
  localStorage.setItem('userEmail', response.data.email);
  localStorage.setItem('userName', response.data.fullName);
  localStorage.setItem('userNickname', response.data.nickname || '');
  localStorage.setItem('userRole', response.data.role || 'User');
  
  if (onSignupSuccess) {
    onSignupSuccess(response.data);
  }
} catch (error) {
  const message = error.response?.data?.message || 'Please check your credentials.';
  setModal({ open: true, title: error.response?.status === 429 ? 'Login Temporarily Locked' : 'Login Failed', message });
}

};

const handleChange = (e) => {
const { name, value, type, checked } = e.target;
setFormData({
  ...formData,
  [name]: type === 'checkbox' ? checked : value
});
if (touched[name]) {
  const error = validateField(name, value, { ...formData, [name]: value });
  setErrors((prev) => ({ ...prev, [name]: error }));
} else if (errors[name]) {
  setErrors((prev) => ({ ...prev, [name]: null }));
}
if (name === 'password' && touched.confirmPassword) {
  const confirmError = validateField(
    'confirmPassword',
    formData.confirmPassword,
    { ...formData, password: value }
  );
  setErrors((prev) => ({ ...prev, confirmPassword: confirmError }));
}
};

const handleBlur = (e) => {
const { name, value } = e.target;
setTouched((prev) => ({ ...prev, [name]: true }));
const error = validateField(name, value, { ...formData, [name]: value });
setErrors((prev) => ({ ...prev, [name]: error }));
};

const handleForgotPassword = async (e) => {
e.preventDefault();
if (!forgotEmail.trim()) {
  setForgotResult('Email is required.');
  return;
}
if (!/\S+@\S+\.\S+/.test(forgotEmail)) {
  setForgotResult('Email format is invalid.');
  return;
}
try {
  setForgotLoading(true);
  const response = await axios.post(`${API_BASE}/account/forgot-password`, {
    email: forgotEmail
  });
  setForgotResult(
    response.data?.message ||
    'A temporary password has been sent to your email address.'
  );
} catch (error) {
  setForgotResult(
    error.response?.status === 429
      ? 'Too many password-reset requests. Try again later.'
      : 'A temporary password has been sent to your email address.'
  );
} finally {
  setForgotLoading(false);
}
};

if (showLogin) {
return (
  <>
    <LoginForm
      onLogin={handleLogin}
      onBackToSignup={() => setShowLogin(false)}
      onForgotPassword={() => {
        setForgotModalOpen(true);
        setForgotResult('');
      }}
    />
    <Modal
      open={modal.open}
      title={modal.title}
      message={modal.message}
      onClose={() => setModal({ open: false, title: '', message: '' })}
    />
    <Modal
      open={forgotModalOpen}
      title="Forgot Password"
      message=""
      onClose={() => {
        setForgotModalOpen(false);
        setForgotEmail('');
        setForgotResult('');
      }}
    >
      <form onSubmit={handleForgotPassword} style={styles.forgotForm}>
        <label style={styles.label}>Email Address</label>
        <input
          type="email"
          value={forgotEmail}
          onChange={(e) => setForgotEmail(e.target.value)}
          style={styles.input}
          placeholder="your.email@example.com"
          required
        />
        <button type="submit" style={styles.button} disabled={forgotLoading}>
          {forgotLoading ? 'Sending...' : 'Send Temporary Password'}
        </button>
      </form>
      {forgotResult && <div style={styles.forgotResult}>{forgotResult}</div>}
    </Modal>
  </>
);
}

return (
<>
<div style={styles.container}>
<div style={styles.card}>
<h1 style={styles.title}>Create Your Account</h1>
<p style={styles.subtitle}>Join our plugin repository community</p>

    <form onSubmit={handleSubmit} style={styles.form}>
      <div style={styles.inputGroup}>
        <label style={styles.label}>Full Name</label>
        <input
          type="text"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          onBlur={handleBlur}
          style={styles.input}
          placeholder="Enter your full name"
        />
        {touched.fullName && errors.fullName && <span style={styles.error}>{errors.fullName}</span>}
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Email Address</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={handleBlur}
          style={styles.input}
          placeholder="your.email@example.com"
        />
        {touched.email && errors.email && <span style={styles.error}>{errors.email}</span>}
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Password</label>
        <input
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          onFocus={() => setShowPasswordRules(true)}
          onBlur={(e) => {
            setShowPasswordRules(false);
            handleBlur(e);
          }}
          style={styles.input}
          placeholder="Create a strong password"
        />
        {touched.password && errors.password && <span style={styles.error}>{errors.password}</span>}
        {showPasswordRules && (
          <div style={styles.requirements}>
            <RequirementItem ok={passwordChecks.minLength} text="At least 6 characters" />
            <RequirementItem ok={passwordChecks.hasLower} text="Contains a lowercase letter" />
            <RequirementItem ok={passwordChecks.hasUpper} text="Contains an uppercase letter" />
            <RequirementItem ok={passwordChecks.hasDigit} text="Contains a number" />
            <RequirementItem ok={passwordChecks.matchesConfirm} text="Matches confirmation" />
          </div>
        )}
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Confirm Password</label>
        <input
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          style={styles.input}
          placeholder="Re-enter your password"
        />
        {touched.confirmPassword && errors.confirmPassword && (
          <span style={styles.error}>{errors.confirmPassword}</span>
        )}
      </div>

      <label style={styles.checkboxRow}>
        <input
          type="checkbox"
          name="agreedToTerms"
          checked={formData.agreedToTerms}
          onChange={handleChange}
        />
        <span>I agree to the terms of service and privacy policy.</span>
      </label>
      {errors.agreedToTerms && <span style={styles.error}>{errors.agreedToTerms}</span>}

      <p style={styles.securityNote}>
        Signup is limited per IP to 5 attempts per hour and 3 successful accounts per 24 hours.
      </p>

      {errors.submitList && errors.submitList.length > 0 && (
        <div style={styles.submitErrorList}>
          <ul style={styles.submitErrorListItems}>
            {errors.submitList.map((err, idx) => (
              <li key={idx} style={styles.submitErrorItem}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        style={{...styles.button, opacity: loading ? 0.6 : 1}}
      >
        {loading ? 'Creating Account...' : 'Sign Up'}
      </button>
    </form>

    <div style={styles.footer}>
      Already have an account?{' '}
      <button onClick={() => setShowLogin(true)} style={styles.linkButton}>
        Login here
      </button>
    </div>
</div>
</div>

<Modal
  open={modal.open}
  title={modal.title}
  message={modal.message}
  onClose={() => setModal({ open: false, title: '', message: '' })}
/>

<Modal
  open={forgotModalOpen}
  title="Forgot Password"
  message=""
  onClose={() => {
    setForgotModalOpen(false);
    setForgotEmail('');
    setForgotResult('');
  }}
>
  <form onSubmit={handleForgotPassword} style={styles.forgotForm}>
    <label style={styles.label}>Email Address</label>
    <input
      type="email"
      value={forgotEmail}
      onChange={(e) => setForgotEmail(e.target.value)}
      style={styles.input}
      placeholder="your.email@example.com"
      required
    />
    <button type="submit" style={styles.button} disabled={loading}>
      Send Temporary Password
    </button>
  </form>
  {forgotResult && <div style={styles.forgotResult}>{forgotResult}</div>}
</Modal>

</>
);
}

function LoginForm({ onLogin, onBackToSignup, onForgotPassword }) {
const [email, setEmail] = useState('');
const [password, setPassword] = useState('');
const [loading, setLoading] = useState(false);

const handleSubmit = async (e) => {
e.preventDefault();
setLoading(true);
await onLogin(email, password);
setLoading(false);
};

return (
<div style={styles.container}>
<div style={styles.card}>
<h1 style={styles.title}>Welcome Back</h1>
<p style={styles.subtitle}>Login to your account</p>

    <form onSubmit={handleSubmit} style={styles.form}>
      <div style={styles.inputGroup}>
        <label style={styles.label}>Email Address</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={styles.input}
          placeholder="your.email@example.com"
          required
        />
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={styles.input}
          placeholder="Enter your password"
          required
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        style={{...styles.button, opacity: loading ? 0.6 : 1}}
      >
        {loading ? 'Logging in...' : 'Login'}
      </button>
      <p style={styles.securityNote}>
        For your security, 10 failed login attempts within 15 minutes trigger a 15-minute IP lockout.
      </p>
    </form>

    <div style={styles.footer}>
      Don't have an account?{' '}
      <button onClick={onBackToSignup} style={styles.linkButton}>
        Sign up here
      </button>
    </div>
    <div style={styles.footer}>
      <button onClick={onForgotPassword} style={styles.linkButton}>
        Forgot password?
      </button>
    </div>
  </div>
</div>

);
}

function RequirementItem({ ok, text }) {
return (
<div style={styles.requirementRow}>
  <span style={{...styles.requirementMark, ...(ok ? styles.requirementOk : styles.requirementBad)}}>
    {ok ? 'ok' : 'x'}
  </span>
  <span style={{...(ok ? styles.requirementOk : styles.requirementBad)}}>
    {text}
  </span>
</div>
);
}

function Modal({ open, title, message, onClose, children }) {
if (!open) return null;
return (
<div style={styles.modalOverlay}>
  <div style={styles.modalBox} role="dialog" aria-modal="true">
    <div style={styles.modalHeader}>
      <h3 style={styles.modalTitle}>{title}</h3>
      <button onClick={onClose} style={styles.modalClose}>×</button>
    </div>
    <div style={styles.modalBody}>
      {message && <p style={styles.modalMessage}>{message}</p>}
      {children}
    </div>
    <div style={styles.modalActions}>
      <button onClick={onClose} style={styles.modalOk}>OK</button>
    </div>
  </div>
</div>
);
}

const styles = {
container: {
minHeight: '100vh',
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
padding: '20px'
},
card: {
background: 'white',
borderRadius: '12px',
padding: '40px',
width: '100%',
maxWidth: '480px',
boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
},
title: {
fontSize: '32px',
fontWeight: 'bold',
color: '#333',
marginBottom: '10px',
textAlign: 'center'
},
subtitle: {
fontSize: '16px',
color: '#666',
marginBottom: '30px',
textAlign: 'center'
},
form: {
display: 'flex',
flexDirection: 'column',
gap: '20px'
},
inputGroup: {
display: 'flex',
flexDirection: 'column',
gap: '8px'
},
label: {
fontSize: '14px',
fontWeight: '600',
color: '#333'
},
input: {
padding: '12px 16px',
fontSize: '16px',
border: '2px solid #e1e8ed',
borderRadius: '8px',
transition: 'border-color 0.3s',
outline: 'none'
},
requirements: {
marginTop: '10px',
padding: '10px 12px',
borderRadius: '6px',
background: '#f8f9fa',
border: '1px solid #e1e8ed'
},
requirementRow: {
display: 'flex',
alignItems: 'center',
gap: '8px',
fontSize: '13px',
marginTop: '6px'
},
requirementMark: {
display: 'inline-block',
minWidth: '22px',
textAlign: 'center',
borderRadius: '4px',
padding: '2px 4px',
fontWeight: '700',
fontSize: '12px',
textTransform: 'uppercase'
},
requirementOk: {
color: '#1e7e34'
},
requirementBad: {
color: '#c0392b'
},
error: {
fontSize: '13px',
color: '#e74c3c',
marginTop: '4px'
},
submitError: {
padding: '12px',
background: '#fee',
border: '1px solid #fcc',
borderRadius: '6px',
color: '#c33',
fontSize: '14px'
},
submitErrorList: {
marginTop: '10px',
padding: '12px',
background: '#fff5f5',
border: '1px solid #fcc',
borderRadius: '6px',
color: '#c33',
fontSize: '14px'
},
submitErrorListItems: {
margin: 0,
paddingLeft: '18px'
},
submitErrorItem: {
marginTop: '6px'
},
button: {
padding: '14px',
fontSize: '16px',
fontWeight: '600',
color: 'white',
background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
transition: 'transform 0.2s',
marginTop: '10px'
},
footer: {
marginTop: '30px',
textAlign: 'center',
color: '#666',
fontSize: '14px'
},
linkButton: {
background: 'none',
border: 'none',
color: '#667eea',
fontWeight: '600',
cursor: 'pointer',
textDecoration: 'underline',
fontSize: '14px'
},
modalOverlay: {
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
background: 'rgba(0,0,0,0.6)',
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
zIndex: 2000
},
modalBox: {
background: 'white',
borderRadius: '10px',
padding: '20px',
width: '90%',
maxWidth: '460px',
boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
},
modalHeader: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
marginBottom: '10px'
},
modalTitle: {
margin: 0,
fontSize: '18px',
color: '#333'
},
modalClose: {
background: 'none',
border: 'none',
fontSize: '22px',
cursor: 'pointer',
color: '#999'
},
modalBody: {
marginTop: '10px'
},
modalMessage: {
margin: 0,
color: '#444'
},
modalActions: {
display: 'flex',
justifyContent: 'flex-end',
marginTop: '20px'
},
modalOk: {
padding: '8px 16px',
background: '#667eea',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer'
},
forgotForm: {
display: 'flex',
flexDirection: 'column',
gap: '12px',
marginTop: '10px'
},
forgotResult: {
marginTop: '12px',
padding: '10px',
background: '#f8f9fa',
border: '1px solid #e1e8ed',
borderRadius: '6px',
color: '#333',
fontSize: '14px'
},
checkboxRow: {
display: 'flex',
alignItems: 'center',
gap: '8px',
fontSize: '14px',
color: '#444',
marginBottom: '6px'
},
securityNote: {
fontSize: '12px',
lineHeight: '1.45',
color: '#64748b',
background: '#f8fafc',
border: '1px solid #e2e8f0',
borderRadius: '6px',
padding: '9px 10px',
margin: '12px 0 0'
}
};

export default SignupPage;
